import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, Building2, Users, FileText, Eye, Edit, Trash2, CheckCircle, XCircle, CreditCard, Activity, StickyNote, Mail, Flag, Download, MoreVertical, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import AccentColorPicker from '../components/ui/AccentColorPicker';
import Modal from '../components/ui/Modal';
import DataGrid from '../components/grid/DataGrid';
import PhoneInput from '../components/forms/PhoneInput';
import SubscriptionManagementModal from '../components/systemadmin/SubscriptionManagementModal';
import CompanyUsersModal from '../components/systemadmin/CompanyUsersModal';
import CompanyActivityModal from '../components/systemadmin/CompanyActivityModal';
import CompanyNotesPanel from '../components/systemadmin/CompanyNotesPanel';
import EmailCompanyModal from '../components/systemadmin/EmailCompanyModal';
import CompanyFeatureFlagsModal from '../components/systemadmin/CompanyFeatureFlagsModal';
import ConfirmDialog from '../components/modals/ConfirmDialog';
import toast from 'react-hot-toast';

interface Company {
  id: number;
  name: string;
  isActive: boolean;
  createdDate: string;
  userCount: number;
  accentColor: string;
  address?: string;
  city?: string;
  email?: string;
  phone?: string;
  state?: string;
  website?: string;
  zipCode?: string;
  // Subscription fields
  subscriptionStatus?: 'Trial' | 'Active' | 'Cancelled' | 'PastDue' | 'Suspended' | null;
  subscriptionPlan?: 'starter' | 'professional' | 'enterprise' | null;
  trialEndDate?: string | null;
  subscriptionStartDate?: string | null;
  mrr?: number;
  stripeCustomerId?: string | null;
  lastLoginDate?: string | null;
  lastActivityDate?: string | null;
}


interface EditCompanyForm {
  name: string;
  accentColor: string;
  address: string;
  city: string;
  email: string;
  phone: string;
  state: string;
  website: string;
  zipCode: string;
}

interface SuperAdminStats {
  totalCompanies: number;
  totalUsers: number;
  totalWorkOrders: number;
  recentCompanies: Company[];
}

const SuperAdminPage: React.FC = () => {
  const { user, isLoading: authLoading, isImpersonating } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [stats, setStats] = useState<SuperAdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteFinalConfirm, setShowDeleteFinalConfirm] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);
  const [companyToDeactivate, setCompanyToDeactivate] = useState<Company | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedCompanyForSubscription, setSelectedCompanyForSubscription] = useState<Company | null>(null);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [selectedCompanyForUsers, setSelectedCompanyForUsers] = useState<Company | null>(null);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [selectedCompanyForActivity, setSelectedCompanyForActivity] = useState<Company | null>(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedCompanyForNotes, setSelectedCompanyForNotes] = useState<Company | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedCompanyForEmail, setSelectedCompanyForEmail] = useState<Company | null>(null);
  const [showFeatureFlagsModal, setShowFeatureFlagsModal] = useState(false);
  const [selectedCompanyForFeatureFlags, setSelectedCompanyForFeatureFlags] = useState<Company | null>(null);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<Set<number>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [isBulkToggleModalOpen, setIsBulkToggleModalOpen] = useState(false);
  const [bulkToggleAction, setBulkToggleAction] = useState<boolean | null>(null);
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);

  // Filter companies based on search term and status
  const filteredCompanies = useMemo(() => {
    let filtered = companies;
    
    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(company => 
        statusFilter === 'active' ? company.isActive : !company.isActive
      );
    }
    
    // Filter by search term
    if (searchTerm.trim()) {
      filtered = filtered.filter(company =>
        company.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered;
  }, [companies, searchTerm, statusFilter]);

  // Paginate filtered companies
  const paginatedCompanies = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredCompanies.slice(startIndex, startIndex + pageSize);
  }, [filteredCompanies, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredCompanies.length / pageSize);

  const [editForm, setEditForm] = useState<EditCompanyForm>({
    name: '',
    accentColor: '#3B82F6',
    address: '',
    city: '',
    email: '',
    phone: '',
    state: '',
    website: '',
    zipCode: ''
  });

  useEffect(() => {
    // Wait for auth context to finish loading
    if (authLoading) {
      console.log('SuperAdminPage: Waiting for auth to load...');
      return;
    }

    // Don't load data if still impersonating
    if (isImpersonating) {
      console.log('SuperAdminPage: Still impersonating, skipping data load');
      return;
    }

    const isSystemAdmin = typeof window !== 'undefined' && localStorage.getItem('isSystemAdmin') === 'true';
    const hasValidUser = user && (user.roleName === 'SuperAdmin' || user.roleName === 'SystemAdmin');
    
    if (!isSystemAdmin && !hasValidUser) {
      console.log('SuperAdminPage: Not authorized, redirecting to dashboard');
      window.location.href = '/dashboard';
      return;
    }

    // Ensure we have a valid user before loading
    if (!user) {
      console.log('SuperAdminPage: No user object yet, waiting...');
      return;
    }
    
    console.log('SuperAdminPage: Loading data...', { 
      isSystemAdmin, 
      userRole: user?.roleName, 
      isImpersonating,
      hasToken: !!localStorage.getItem('token')
    });
    
    // Clear any cached queries that might have been from impersonation
    queryClient.invalidateQueries();
    
    loadData();
  }, [user, authLoading, isImpersonating, queryClient]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Verify we have the correct token before making the request
      const token = localStorage.getItem('token');
      const isSystemAdmin = localStorage.getItem('isSystemAdmin') === 'true';
      
      if (!token || !isSystemAdmin) {
        console.error('Missing token or system admin flag');
        toast.error('Authentication error. Please log in again.');
        return;
      }

      console.log('Loading super admin data...');
      const [companiesResponse, statsResponse] = await Promise.all([
        apiService.getSuperAdminCompanies(),
        apiService.getSuperAdminStats()
      ]);

      console.log('Companies response:', companiesResponse);
      console.log('Stats response:', statsResponse);

      if (companiesResponse.success) {
        const companiesData = companiesResponse.data || [];
        console.log(`Loaded ${companiesData.length} companies`);
        setCompanies(companiesData);
        
        if (companiesData.length === 0) {
          console.warn('No companies returned from API');
          toast.warning('No companies found. Please check if you have the correct permissions.');
        }
      } else {
        console.error('Failed to load companies:', companiesResponse.message || 'Unknown error');
        toast.error(`Error loading companies: ${companiesResponse.message || 'Unknown error'}`);
        setCompanies([]); // Clear companies on error
      }

      if (statsResponse.success) {
        setStats(statsResponse.data);
      } else {
        console.error('Failed to load stats:', statsResponse.message || 'Unknown error');
      }
    } catch (error: any) {
      console.error('Error loading super admin data:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error occurred';
      console.error('Error details:', {
        message: errorMessage,
        status: error?.response?.status,
        data: error?.response?.data
      });
      toast.error(`Error loading data: ${errorMessage}`);
      setCompanies([]); // Clear companies on error
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Helper function to calculate health status
  const getHealthStatus = (company: Company): 'Healthy' | 'At Risk' | 'Inactive' => {
    if (!company.lastLoginDate) {
      return 'Inactive';
    }

    const lastLogin = new Date(company.lastLoginDate);
    const daysSinceLogin = (Date.now() - lastLogin.getTime()) / (1000 * 60 * 60 * 24);

    // If no login in last 60 days, consider inactive
    if (daysSinceLogin > 60) {
      return 'Inactive';
    }

    // If no activity in last 30 days, consider at risk
    if (!company.lastActivityDate) {
      return 'At Risk';
    }

    const lastActivity = new Date(company.lastActivityDate);
    const daysSinceActivity = (Date.now() - lastActivity.getTime()) / (1000 * 60 * 60 * 24);

    if (daysSinceActivity > 30) {
      return 'At Risk';
    }

    return 'Healthy';
  };

  // Helper function to get health status badge
  const getHealthStatusBadge = (company: Company) => {
    const status = getHealthStatus(company);
    const bgColor = status === 'Healthy' 
      ? 'bg-green-100 dark:bg-green-900/20' 
      : status === 'At Risk' 
      ? 'bg-yellow-100 dark:bg-yellow-900/20' 
      : 'bg-red-100 dark:bg-red-900/20';
    const textColor = status === 'Healthy' 
      ? 'text-green-800 dark:text-green-400' 
      : status === 'At Risk' 
      ? 'text-yellow-800 dark:text-yellow-400' 
      : 'text-red-800 dark:text-red-400';

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
        {status}
      </span>
    );
  };

  // Helper function to format relative time
  const formatRelativeTime = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
    return `${Math.floor(diffDays / 365)}y ago`;
  };

  // Helper function to get subscription status badge
  const getSubscriptionStatusBadge = (company: Company) => {
    if (!company.subscriptionStatus) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
          No Subscription
        </span>
      );
    }

    const status = company.subscriptionStatus;
    const isTrial = status === 'Trial';
    const isActive = status === 'Active';
    const isPastDue = status === 'PastDue';
    const isCancelled = status === 'Cancelled';

    let bgColor = 'bg-gray-100';
    let textColor = 'text-gray-800';
    let darkBg = 'dark:bg-gray-700';
    let darkText = 'dark:text-gray-300';

    if (isTrial) {
      bgColor = 'bg-yellow-100';
      textColor = 'text-yellow-800';
      darkBg = 'dark:bg-yellow-900/20';
      darkText = 'dark:text-yellow-400';
    } else if (isActive) {
      bgColor = 'bg-green-100';
      textColor = 'text-green-800';
      darkBg = 'dark:bg-green-900/20';
      darkText = 'dark:text-green-400';
    } else if (isPastDue) {
      bgColor = 'bg-red-100';
      textColor = 'text-red-800';
      darkBg = 'dark:bg-red-900/20';
      darkText = 'dark:text-red-400';
    } else if (isCancelled) {
      bgColor = 'bg-gray-100';
      textColor = 'text-gray-800';
      darkBg = 'dark:bg-gray-700';
      darkText = 'dark:text-gray-300';
    }

    const daysRemaining = isTrial && company.trialEndDate
      ? Math.ceil((new Date(company.trialEndDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
      : null;

    return (
      <div className="flex flex-col gap-1">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor} ${darkBg} ${darkText}`}>
          {status}
        </span>
        {isTrial && daysRemaining !== null && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {daysRemaining > 0 ? `${daysRemaining} days left` : 'Expired'}
          </span>
        )}
        {company.subscriptionPlan && (
          <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
            {company.subscriptionPlan}
          </span>
        )}
      </div>
    );
  };

  // Bulk selection handlers
  const handleSelectCompany = (companyId: number) => {
    setSelectedCompanyIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(companyId)) {
        newSet.delete(companyId);
      } else {
        newSet.add(companyId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedCompanyIds.size === paginatedCompanies.length) {
      setSelectedCompanyIds(new Set());
    } else {
      setSelectedCompanyIds(new Set(paginatedCompanies.map(c => c.id)));
    }
  };

  const handleBulkToggleStatus = async (activate: boolean) => {
    if (selectedCompanyIds.size === 0) {
      toast.error('Please select at least one company');
      return;
    }

    setBulkToggleAction(activate);
    setIsBulkToggleModalOpen(true);
  };

  const confirmBulkToggle = async () => {
    if (bulkToggleAction === null || selectedCompanyIds.size === 0) {
      return;
    }

    try {
      setBulkActionLoading(true);
      const response = await apiService.bulkToggleCompanyStatus(Array.from(selectedCompanyIds), bulkToggleAction);
      if (response.success) {
        toast.success(`${response.data.successful} company(ies) ${bulkToggleAction ? 'activated' : 'deactivated'} successfully`);
        if (response.data.failed > 0) {
          toast.error(`${response.data.failed} company(ies) failed to update`);
        }
        setSelectedCompanyIds(new Set());
        setIsBulkToggleModalOpen(false);
        setBulkToggleAction(null);
        loadCompanies();
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to update companies');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkExport = async () => {
    if (selectedCompanyIds.size === 0) {
      toast.error('Please select at least one company');
      return;
    }

    try {
      setBulkActionLoading(true);
      const blob = await apiService.bulkExportCompanies(Array.from(selectedCompanyIds), false);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `companies_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Export completed successfully');
      setSelectedCompanyIds(new Set());
    } catch (error: any) {
      toast.error(error.message || 'Failed to export companies');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkEmail = () => {
    if (selectedCompanyIds.size === 0) {
      toast.error('Please select at least one company');
      return;
    }
    setShowBulkEmailModal(true);
  };

  // Define columns for DataGrid
  const columns = [
    {
      id: 'select',
      header: () => (
        <input
          type="checkbox"
          checked={selectedCompanyIds.size > 0 && selectedCompanyIds.size === paginatedCompanies.length}
          onChange={handleSelectAll}
          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
        />
      ),
      sortable: false,
      sticky: 'left' as const,
      stickyWidth: '60px',
      cell: (value: any, company: Company) => (
        <input
          type="checkbox"
          checked={selectedCompanyIds.has(company.id)}
          onChange={() => handleSelectCompany(company.id)}
          onClick={(e) => e.stopPropagation()}
          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
        />
      )
    },
    {
      id: 'name',
      header: 'Company',
      sortable: true,
      cell: (value: string, company: Company) => (
        <div className="text-sm text-gray-900 dark:text-gray-100">
          {company.name}
        </div>
      )
    },
    {
      id: 'subscriptionStatus',
      header: 'Subscription',
      sortable: false,
      cell: (value: any, company: Company) => getSubscriptionStatusBadge(company)
    },
    {
      id: 'userCount',
      header: 'Users',
      sortable: true,
      cell: (value: number) => (
        <div className="text-sm text-gray-900 dark:text-gray-100">
          {value}
        </div>
      )
    },
    {
      id: 'healthStatus',
      header: 'Health',
      sortable: false,
      cell: (value: any, company: Company) => getHealthStatusBadge(company)
    },
    {
      id: 'lastActivityDate',
      header: 'Last Activity',
      sortable: true,
      cell: (value: string | null | undefined, company: Company) => (
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {formatRelativeTime(company.lastActivityDate || company.lastLoginDate)}
        </div>
      )
    },
    {
      id: 'createdDate',
      header: 'Created',
      sortable: true,
      cell: (value: string) => (
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {new Date(value).toLocaleDateString()}
        </div>
      )
    },
    {
      id: 'isActive',
      header: 'Status',
      sortable: true,
      cell: (value: boolean, company: Company) => (
        <div className="relative group">
          <div className="group-hover:opacity-0 transition-opacity duration-200">
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                value
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                  : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
              }`}
            >
              {value ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-end gap-2 pr-2" onClick={(e)=>e.stopPropagation()}>
            <button 
              onClick={() => {
                setSelectedCompanyForSubscription(company);
                setShowSubscriptionModal(true);
              }}
              className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              title="Manage Subscription"
            >
              <CreditCard className="h-4 w-4"/>
            </button>
            <button 
              onClick={() => {
                setSelectedCompanyForUsers(company);
                setShowUsersModal(true);
              }}
              className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              title="Manage Users"
            >
              <Users className="h-4 w-4"/>
            </button>
            <button 
              onClick={() => {
                setSelectedCompanyForActivity(company);
                setShowActivityModal(true);
              }}
              className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              title="View Activity & Usage"
            >
              <Activity className="h-4 w-4"/>
            </button>
            <button 
              onClick={() => {
                setSelectedCompanyForNotes(company);
                setShowNotesModal(true);
              }}
              className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              title="View Notes"
            >
              <StickyNote className="h-4 w-4"/>
            </button>
            <button 
              onClick={() => {
                setSelectedCompanyForEmail(company);
                setShowEmailModal(true);
              }}
              className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              title="Send Email"
            >
              <Mail className="h-4 w-4"/>
            </button>
            <button 
              onClick={() => {
                setSelectedCompanyForFeatureFlags(company);
                setShowFeatureFlagsModal(true);
              }}
              className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              title="Manage Feature Flags"
            >
              <Flag className="h-4 w-4"/>
            </button>
            <button 
              onClick={() => handleEditCompany(company)}
              className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              title="Edit Company"
            >
              <Edit className="h-4 w-4"/>
            </button>
            <button 
              onClick={() => handleToggleCompanyStatus(company)}
              className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              title={company.isActive ? 'Deactivate Company' : 'Activate Company'}
            >
              {company.isActive ? <XCircle className="h-4 w-4"/> : <CheckCircle className="h-4 w-4"/>}
            </button>
            <button 
              onClick={() => handleDeleteClick(company)}
              className="h-7 px-2 inline-flex items-center border rounded-md text-red-600 dark:text-red-400 border-red-300 dark:border-red-600 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/20"
              title="Delete Company"
            >
              <Trash2 className="h-4 w-4"/>
            </button>
          </div>
        </div>
      )
    }
  ];


  const handleToggleCompanyStatus = (company: Company) => {
    setCompanyToDeactivate(company);
    setShowDeactivateConfirm(true);
  };

  const handleDeactivateConfirm = async () => {
    if (!companyToDeactivate) return;

    try {
      const response = await apiService.toggleSuperAdminCompanyStatus(companyToDeactivate.id);
      if (response.success) {
        setShowDeactivateConfirm(false);
        setCompanyToDeactivate(null);
        toast.success(`Company "${companyToDeactivate.name}" ${companyToDeactivate.isActive ? 'deactivated' : 'activated'} successfully!`);
        loadData();
      } else {
        toast.error('Failed to update company status');
      }
    } catch (error) {
      console.error('Error updating company status:', error);
      toast.error('Failed to update company status');
    }
  };

  const handleEditCompany = (company: Company) => {
    setEditingCompany(company);
    setEditForm({
      name: company.name,
      accentColor: company.accentColor || '#3B82F6',
      adminEmail: '',
      adminPassword: '',
      address: company.address || '',
      city: company.city || '',
      email: company.email || '',
      phone: company.phone || '',
      state: company.state || '',
      website: company.website || '',
      zipCode: company.zipCode || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCompany) return;

    try {
      const response = await apiService.updateSuperAdminCompany(editingCompany.id, {
        name: editForm.name,
        isActive: editingCompany.isActive,
        accentColor: editForm.accentColor,
        address: editForm.address,
        city: editForm.city,
        email: editForm.email,
        phone: editForm.phone,
        state: editForm.state,
        website: editForm.website,
        zipCode: editForm.zipCode
      });
      
      if (response.success) {
        setShowEditModal(false);
        setEditingCompany(null);
        toast.success(`Company "${editForm.name}" updated successfully!`);
        loadData();
        // Invalidate company query to refresh the header accent color
        queryClient.invalidateQueries({ queryKey: ['company'] });
      } else {
        toast.error('Failed to update company');
      }
    } catch (error) {
      console.error('Error updating company:', error);
      toast.error('Failed to update company');
    }
  };

  const handleDeleteClick = (company: Company) => {
    setCompanyToDelete(company);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setDeleteConfirmText('');
    setShowDeleteFinalConfirm(true);
  };

  const handleDeleteFinalConfirm = async () => {
    if (!companyToDelete) return;

    try {
      const response = await apiService.deleteSuperAdminCompany(companyToDelete.id);
      if (response.success) {
        setShowDeleteFinalConfirm(false);
        setCompanyToDelete(null);
        toast.success(`Company "${companyToDelete.name}" deleted successfully!`);
        loadData();
      } else {
        toast.error('Failed to delete company');
      }
    } catch (error) {
      console.error('Error deleting company:', error);
      toast.error('Failed to delete company');
    }
  };

  const handleResendWelcomeEmail = async (companyId: number) => {
    setResendingEmail(true);
    try {
      const response = await apiService.resendWelcomeEmail(companyId);
      if (response.success) {
        toast.success(`Welcome email sent successfully to ${response.data.AdminEmail}`);
      } else {
        toast.error('Failed to send welcome email');
      }
    } catch (error) {
      console.error('Error resending welcome email:', error);
      toast.error('Failed to send welcome email');
    } finally {
      setResendingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-accent-color"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Super Admin Dashboard</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Manage companies and system-wide settings</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-2 bg-accent-color-100 rounded-lg">
                  <Building2 className="h-6 w-6 text-accent-color" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Companies</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.totalCompanies}</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Users className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Users</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.totalUsers}</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FileText className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Work Orders</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.totalWorkOrders}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Companies Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Companies</h2>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/systemadmin/data-retention')}
                className="btn-secondary flex items-center space-x-2"
              >
                <Trash2 className="h-4 w-4" />
                <span>Data Retention</span>
              </button>
              <button
                onClick={() => navigate('/systemadmin/companies/create')}
                className="btn-primary flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Create Company</span>
              </button>
            </div>
          </div>

          {/* Status Filter */}
          <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as 'all' | 'active' | 'inactive');
                setCurrentPage(1); // Reset to first page when filtering
              }}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
            >
              <option value="all">All Companies</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Showing {filteredCompanies.length} of {companies.length} companies
            </span>
          </div>

          {/* Bulk Actions Toolbar */}
          {selectedCompanyIds.size > 0 && (
            <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-blue-50 dark:bg-blue-900/20 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {selectedCompanyIds.size} company{selectedCompanyIds.size !== 1 ? 'ies' : ''} selected
                </span>
                <button
                  onClick={() => setSelectedCompanyIds(new Set())}
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                >
                  Clear selection
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleBulkToggleStatus(true)}
                  disabled={bulkActionLoading}
                  className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  Activate
                </button>
                <button
                  onClick={() => handleBulkToggleStatus(false)}
                  disabled={bulkActionLoading}
                  className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                  Deactivate
                </button>
                <button
                  onClick={handleBulkEmail}
                  disabled={bulkActionLoading}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  Send Email
                </button>
                <button
                  onClick={handleBulkExport}
                  disabled={bulkActionLoading}
                  className="px-3 py-1.5 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Export CSV
                </button>
              </div>
            </div>
          )}

          <DataGrid
            data={paginatedCompanies}
            columns={columns}
            searchTerm={searchTerm}
            onSearch={handleSearch}
            currentPage={currentPage}
            pageSize={pageSize}
            totalCount={filteredCompanies.length}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            searchPlaceholder="Search companies..."
            emptyMessage="No companies found"
            getRowClassName={() => 'group'}
          />
        </div>


        {/* Edit Company Modal */}
        {showEditModal && editingCompany && (
          <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Edit Company" maxWidth="2xl">
            <form onSubmit={handleUpdateCompany} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Company Name</label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Accent Color</label>
                    <div className="flex items-center space-x-3 mb-3">
                      <input
                        type="color"
                        value={editForm.accentColor}
                        onChange={(e) => setEditForm({ ...editForm, accentColor: e.target.value })}
                        className="h-10 w-20 border border-gray-300 rounded-md shadow-sm focus:ring-accent-color focus:border-accent-color cursor-pointer"
                      />
                      <span className="text-sm text-gray-500 dark:text-gray-400 font-mono">{editForm.accentColor}</span>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Quick Colors</label>
                      <div className="grid grid-cols-6 gap-2">
                        {[
                          '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899',
                          '#06B6D4', '#84CC16', '#F97316', '#6366F1', '#14B8A6', '#A855F7'
                        ].map((color) => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setEditForm({ ...editForm, accentColor: color })}
                            className={`h-8 w-8 rounded-md border-2 transition-all hover:scale-110 ${
                              editForm.accentColor === color 
                                ? 'border-gray-900 ring-2 ring-gray-300' 
                                : 'border-gray-300 hover:border-gray-400'
                            }`}
                            style={{ backgroundColor: color }}
                            title={color}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Company Contact Information */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Company Contact Information</h3>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Company Email</label>
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Phone Number</label>
                      <PhoneInput
                        value={editForm.phone}
                        onChange={(value) => setEditForm({ ...editForm, phone: value })}
                        placeholder="Enter phone number"
                      />
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Website</label>
                      <input
                        type="url"
                        value={editForm.website}
                        onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="https://example.com"
                      />
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Address</label>
                      <input
                        type="text"
                        value={editForm.address}
                        onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="Street address"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">City</label>
                        <input
                          type="text"
                          value={editForm.city}
                          onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">State</label>
                        <input
                          type="text"
                          value={editForm.state}
                          onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Zip Code</label>
                      <input
                        type="text"
                        value={editForm.zipCode}
                        onChange={(e) => setEditForm({ ...editForm, zipCode: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="12345"
                      />
                    </div>
                  </div>

                  {/* Resend Welcome Email Section */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Admin User Management</h4>
                    <div className="bg-accent-color-50 dark:bg-accent-color-900/20 border border-accent-color-200 dark:border-accent-color-800 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-accent-color-800 dark:text-accent-color-200 font-medium">Resend Welcome Email</p>
                          <p className="text-xs text-accent-color dark:text-accent-color-300 mt-1">
                            Send a new welcome email with temporary login credentials to the company admin
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleResendWelcomeEmail(editingCompany.id)}
                          disabled={resendingEmail}
                          className="px-4 py-2 bg-accent-color hover:bg-accent-color-600 disabled:bg-accent-color-400 text-white text-sm font-medium rounded-md transition-colors duration-200 flex items-center space-x-2"
                        >
                          {resendingEmail ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              <span>Sending...</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              <span>Resend Email</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowEditModal(false);
                        setEditingCompany(null);
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                    >
                      Update Company
                    </button>
                  </div>
            </form>
          </Modal>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && companyToDelete && (
          <Modal isOpen={showDeleteConfirm} onClose={() => { setShowDeleteConfirm(false); setCompanyToDelete(null); }} title="Delete Company" maxWidth="sm">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20 mb-4">
                <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="mb-6">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  Are you sure you want to delete <strong className="text-gray-900 dark:text-gray-100">{companyToDelete.name}</strong>?
                </p>
                <p className="text-sm text-red-600 dark:text-red-400 font-medium">
                  This action will deactivate the company and cannot be undone.
                </p>
              </div>
              <div className="flex justify-center space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setCompanyToDelete(null);
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                >
                  Continue to Final Confirmation
                </button>
              </div>
            </div>
          </Modal>
        )}

        {/* Final Delete Confirmation Modal */}
        {showDeleteFinalConfirm && companyToDelete && (
          <Modal isOpen={showDeleteFinalConfirm} onClose={() => { setShowDeleteFinalConfirm(false); setCompanyToDelete(null); }} title="FINAL CONFIRMATION" maxWidth="sm">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20 mb-4">
                <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="mb-6">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  You are about to <strong className="text-red-600 dark:text-red-400">PERMANENTLY DELETE</strong>:
                </p>
                <p className="text-lg font-bold text-red-600 dark:text-red-400 mb-4">{companyToDelete.name}</p>
                <p className="text-sm text-red-600 dark:text-red-400 font-medium mb-4">
                  ⚠️ This action CANNOT be undone! ⚠️
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  Type the company name to confirm:
                </p>
                <input
                  type="text"
                  placeholder={companyToDelete.name}
                  value={deleteConfirmText}
                  className="mt-2 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                />
              </div>
              <div className="flex justify-center space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteFinalConfirm(false);
                    setCompanyToDelete(null);
                    setDeleteConfirmText('');
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteFinalConfirm(false);
                    setDeleteConfirmText('');
                    handleDeleteFinalConfirm();
                  }}
                  disabled={deleteConfirmText !== companyToDelete.name}
                  className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                    deleteConfirmText === companyToDelete.name
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-gray-400 cursor-not-allowed'
                  }`}
                >
                  YES, DELETE FOREVER
                </button>
              </div>
            </div>
          </Modal>
        )}

        {/* Deactivate Confirmation Modal */}
        {showDeactivateConfirm && companyToDeactivate && (
          <Modal isOpen={showDeactivateConfirm} onClose={() => { setShowDeactivateConfirm(false); setCompanyToDeactivate(null); }} title={companyToDeactivate.isActive ? "Deactivate Company" : "Activate Company"} maxWidth="sm">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 dark:bg-orange-900/20 mb-4">
                {companyToDeactivate.isActive ? (
                  <XCircle className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                ) : (
                  <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                )}
              </div>
              <div className="mb-6">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  Are you sure you want to {companyToDeactivate.isActive ? 'deactivate' : 'activate'}:
                </p>
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">{companyToDeactivate.name}</p>
                {companyToDeactivate.isActive ? (
                  <p className="text-sm text-orange-600 dark:text-orange-400 font-medium">
                    This will disable the company and prevent users from accessing it.
                  </p>
                ) : (
                  <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                    This will enable the company and allow users to access it.
                  </p>
                )}
              </div>
              <div className="flex justify-center space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeactivateConfirm(false);
                    setCompanyToDeactivate(null);
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeactivateConfirm}
                  className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                    companyToDeactivate.isActive 
                      ? 'bg-orange-600 hover:bg-orange-700' 
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {companyToDeactivate.isActive ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </div>
          </Modal>
        )}

        {/* Subscription Management Modal */}
        {showSubscriptionModal && selectedCompanyForSubscription && (
          <SubscriptionManagementModal
            isOpen={showSubscriptionModal}
            onClose={() => {
              setShowSubscriptionModal(false);
              setSelectedCompanyForSubscription(null);
            }}
            companyId={selectedCompanyForSubscription.id}
            companyName={selectedCompanyForSubscription.name}
          />
        )}

        {/* Company Users Modal */}
        {showUsersModal && selectedCompanyForUsers && (
          <CompanyUsersModal
            isOpen={showUsersModal}
            onClose={() => {
              setShowUsersModal(false);
              setSelectedCompanyForUsers(null);
            }}
            companyId={selectedCompanyForUsers.id}
            companyName={selectedCompanyForUsers.name}
          />
        )}
        {showActivityModal && selectedCompanyForActivity && (
          <CompanyActivityModal
            isOpen={showActivityModal}
            onClose={() => {
              setShowActivityModal(false);
              setSelectedCompanyForActivity(null);
            }}
            companyId={selectedCompanyForActivity.id}
            companyName={selectedCompanyForActivity.name}
          />
        )}
        {showNotesModal && selectedCompanyForNotes && (
          <Modal
            isOpen={showNotesModal}
            onClose={() => {
              setShowNotesModal(false);
              setSelectedCompanyForNotes(null);
            }}
            title={`Company Notes - ${selectedCompanyForNotes.name}`}
            maxWidth="4xl"
          >
            <CompanyNotesPanel
              companyId={selectedCompanyForNotes.id}
              companyName={selectedCompanyForNotes.name}
            />
          </Modal>
        )}
        {showEmailModal && selectedCompanyForEmail && (
          <EmailCompanyModal
            isOpen={showEmailModal}
            onClose={() => {
              setShowEmailModal(false);
              setSelectedCompanyForEmail(null);
            }}
            companyId={selectedCompanyForEmail.id}
            companyName={selectedCompanyForEmail.name}
          />
        )}
        {showFeatureFlagsModal && selectedCompanyForFeatureFlags && (
          <CompanyFeatureFlagsModal
            isOpen={showFeatureFlagsModal}
            onClose={() => {
              setShowFeatureFlagsModal(false);
              setSelectedCompanyForFeatureFlags(null);
            }}
            companyId={selectedCompanyForFeatureFlags.id}
            companyName={selectedCompanyForFeatureFlags.name}
          />
        )}

        {/* Bulk Email Modal */}
        {showBulkEmailModal && (
          <Modal
            isOpen={showBulkEmailModal}
            onClose={() => setShowBulkEmailModal(false)}
            title={`Send Email to ${selectedCompanyIds.size} Companies`}
            maxWidth="2xl"
          >
            <BulkEmailForm
              companyIds={Array.from(selectedCompanyIds)}
              onSuccess={() => {
                setShowBulkEmailModal(false);
                setSelectedCompanyIds(new Set());
                loadCompanies();
              }}
              onCancel={() => setShowBulkEmailModal(false)}
            />
          </Modal>
        )}

        {/* Bulk Toggle Status Confirmation Modal */}
        <ConfirmDialog
          isOpen={isBulkToggleModalOpen}
          onClose={() => {
            setIsBulkToggleModalOpen(false);
            setBulkToggleAction(null);
          }}
          onConfirm={confirmBulkToggle}
          title={bulkToggleAction ? 'Activate Companies' : 'Deactivate Companies'}
          message={`Are you sure you want to ${bulkToggleAction ? 'activate' : 'deactivate'} ${selectedCompanyIds.size} company(ies)?`}
          confirmText={bulkToggleAction ? 'Activate' : 'Deactivate'}
          cancelText="Cancel"
          variant={bulkToggleAction ? 'info' : 'warning'}
          isLoading={bulkActionLoading}
        />
      </div>
    </div>
  );
};

// Bulk Email Form Component
const BulkEmailForm: React.FC<{
  companyIds: number[];
  onSuccess: () => void;
  onCancel: () => void;
}> = ({ companyIds, onSuccess, onCancel }) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) {
      toast.error('Please fill in both subject and body');
      return;
    }

    try {
      setSending(true);
      const response = await apiService.bulkSendEmailToCompanies(companyIds, {
        subject: subject.trim(),
        body: body.trim()
      });

      if (response.success) {
        toast.success(`Email sent to ${response.data.successful} company(ies) successfully`);
        if (response.data.failed > 0) {
          toast.error(`${response.data.failed} company(ies) failed to receive email`);
        }
        onSuccess();
      } else {
        toast.error('Failed to send bulk email');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to send bulk email');
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Subject
        </label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Message
        </label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={8}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
          required
        />
      </div>
      <div className="flex justify-end gap-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={sending}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {sending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Mail className="w-4 h-4" />
              Send Email
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default SuperAdminPage;
